import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import searchSpotifyArtistsAdmin from '../../../api/graphql/queries/searchSpotifyArtistsAdmin';
import getSpotifyArtistsByIds from '../../../api/graphql/queries/getSpotifyArtistsByIds';
import { describeArtist, idsFromSelection, selectionFromIds } from '../../../utils/spotifyArtistPicker';

const SEARCH_DELAY_MS = 350;

/* Which Spotify artists an artist page is. Usually one; a collab or a special
 * show names each artist in it (`charli-xcx-troye-sivan-sweat` -> Charli xcx
 * and Troye Sivan), and a fan who favourites the page counts for each.
 *
 * Stored in the page description as `spotifyArtistIds`. It is how fans who
 * favourite this page reach Spotify artists in the Pulse export; a page left
 * empty shows up there as unresolved. The followers and popularity under each
 * option are there to tell namesakes apart: pick the one actually on the
 * lineup. */
const SpotifyArtistsPicker = ({ value, onChange }) => {
  const [selection, setSelection] = useState([]);
  const timer = useRef(null);

  // Name the ids the page already holds. Only when they change from outside
  // (page load); a change made here is already in `selection`.
  useEffect(() => {
    const ids = Array.isArray(value) ? value : [];
    if (idsFromSelection(selection).join() === ids.join()) return undefined;
    let cancelled = false;
    getSpotifyArtistsByIds(ids).then((artists) => {
      if (!cancelled) setSelection(selectionFromIds(ids, artists));
    });
    return () => { cancelled = true; };
  }, [value]);

  // Every search goes to Spotify, so wait for the editor to stop typing.
  const loadOptions = term => new Promise((resolve) => {
    clearTimeout(timer.current);
    if (!term || term.trim().length < 2) { resolve([]); return; }
    timer.current = setTimeout(() => { searchSpotifyArtistsAdmin(term.trim()).then(resolve); }, SEARCH_DELAY_MS);
  });

  const handleChange = (picked) => {
    const next = Array.isArray(picked) ? picked : [];
    setSelection(next);
    onChange(idsFromSelection(next));
  };

  const formatOptionLabel = (artist, { context }) => (
    <div className="flex items-center">
      {artist.image
        ? <img src={artist.image} alt="" className="rounded-full mr-2" style={{ width: context === 'menu' ? 32 : 20, height: context === 'menu' ? 32 : 20, objectFit: 'cover' }} />
        : null}
      <div>
        <div>{artist.name || artist.id}</div>
        {context === 'menu' ? <div className="text-xxs text-grey font-normal">{describeArtist(artist)}</div> : null}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {/* react-select renders the input, which the rule cannot see */}
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      <label className="font-americaMonoRegular uppercase text-xs" htmlFor="spotify-artists-picker">
        Spotify artists
        <AsyncSelect
          inputId="spotify-artists-picker"
          className="font-americaMonoRegular text-black font-bold mt-1"
          isMulti
          cacheOptions
          loadOptions={loadOptions}
          value={selection}
          onChange={handleChange}
          getOptionValue={artist => artist.id}
          getOptionLabel={artist => artist.name || artist.id}
          formatOptionLabel={formatOptionLabel}
          placeholder="Search Spotify…"
          noOptionsMessage={({ inputValue }) => (inputValue && inputValue.trim().length >= 2 ? 'No artists found' : 'Type an artist name')}
        />
      </label>
      <p className="text-xxs mt-1">
        The artist this page is on Spotify. For a collab or special show, add each artist in it.
      </p>
    </div>
  );
};

SpotifyArtistsPicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

SpotifyArtistsPicker.defaultProps = {
  value: [],
};

export default SpotifyArtistsPicker;
